class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';
    this.originalPopularTerms = new Map(); // Store original text content

    this.setupEventListeners();

    if (this.predictiveSearchResults) {
      this.ensureSkeletonLoader();
    }
    this.storeOriginalPopularTerms(); // for highlighting popular search terms
  }

  setupEventListeners() {
    this.querySelector('form.search').addEventListener('submit', this.onFormSubmit.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));
    this.querySelector('button[type="reset"]').addEventListener('click', this.clear.bind(this));
    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  storeOriginalPopularTerms() {
    const popularSearchTerms = this.modal
      ? this.modal.querySelectorAll('.popular-search-term')
      : document.querySelectorAll('.popular-search-term');

    popularSearchTerms.forEach((term) => {
      if (!this.originalPopularTerms.has(term)) {
        this.originalPopularTerms.set(term, term.textContent.trim());
      }
    });
  }

  highlightPopularSearchTerms(searchTerm) {
    if (!searchTerm) {
      this.clearPopularSearchHighlights();
      return;
    }
    // Find popular search terms in the modal
    const popularSearchTerms = this.modal
      ? this.modal.querySelectorAll('.popular-search-term')
      : document.querySelectorAll('.popular-search-term');
    popularSearchTerms.forEach((term) => {
      const originalText = this.originalPopularTerms.get(term);
      if (!originalText) return;
      const highlightedText = this.createHighlightedText(originalText, searchTerm);
      term.innerHTML = highlightedText;
    });
  }

  createHighlightedText(text, searchTerm) {
    if (!searchTerm || !text) return text;
    // Escape special regex characters in searchTerm
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (searchTerm.length === 1) {
      // For single character: highlight all instances of that character
      const singleCharRegex = new RegExp(`(${escapedSearchTerm})`, 'gi');
      return text.replace(singleCharRegex, '<span class="search-highlight">$1</span>');
    } else {
      // For multiple characters: only highlight consecutive sequences
      const consecutiveRegex = new RegExp(`(${escapedSearchTerm})`, 'gi');
      return text.replace(consecutiveRegex, '<span class="search-highlight">$1</span>');
    }
  }

  clearPopularSearchHighlights() {
    const popularSearchTerms = this.modal
      ? this.modal.querySelectorAll('.popular-search-term')
      : document.querySelectorAll('.popular-search-term');
    popularSearchTerms.forEach((term) => {
      const originalText = this.originalPopularTerms.get(term);
      if (originalText) {
        term.textContent = originalText;
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    // this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    // Highlight popular search terms based on current search term
    this.highlightPopularSearchTerms(this.searchTerm);

    if (!this.searchTerm.length) {
      this.clear();
      this.close(true);

      // Remove is-empty class when search is cleared
      const searchWrapper =
        this.closest('.search-modal')?.querySelector('.search-recommendations-wrapper') ||
        document.querySelector('.search-recommendations-wrapper');
      if (searchWrapper) {
        searchWrapper.classList.remove('is-empty');
      }

      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');

    // Refresh original popular terms in case they were dynamically added
    this.storeOriginalPopularTerms();

    const currentSearchTerm = this.getQuery();

    // Highlight popular search terms even if no search term
    this.highlightPopularSearchTerms(currentSearchTerm);

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');

    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    fetch(
      `${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent(
        'resources[limit]'
      )}=${perPage}&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        /*
        this.cachedResults[queryKey] = resultsMarkup;
        */
        this.renderSearchResults(resultsMarkup);

        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }

        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    // Ensure skeleton loader exists after content replacement
    this.ensureSkeletonLoader();

    // Limit products shown based on screen size
    this.limitProductsShown();

    this.setAttribute('results', true);

    // Toggle is-empty class on search-recommendations-wrapper
    this.toggleEmptyState();

    this.setLiveRegionResults();
    this.open();
  }

  ensureSkeletonLoader() {
    // Check if skeleton loader already exists
    let skeletonLoader = this.predictiveSearchResults.querySelector('.predictive-search__loading');

    if (!skeletonLoader) {
      // Create skeleton loader if it doesn't exist
      skeletonLoader = document.createElement('div');
      skeletonLoader.className = 'predictive-search__loading';
      skeletonLoader.innerHTML = `
        <div class="skeleton-loader">
          <div class="skeleton-header"></div>
          <div class="skeleton-product-grid">
            ${Array.from(
              { length: 6 },
              (_, i) => `
              <div class="skeleton-product-item">
                <div class="skeleton-image"></div>
                <div class="skeleton-content">
                  <div class="skeleton-title"></div>
                  <div class="skeleton-price"></div>
                </div>
              </div>
            `
            ).join('')}
          </div>
        </div>
      `;
      this.predictiveSearchResults.appendChild(skeletonLoader);
    }
  }

  limitProductsShown() {
    const productList = this.querySelector('#predictive-search-results-products-list');
    if (!productList) return;

    const productItems = productList.querySelectorAll('li');
    const isDesktop = window.innerWidth >= 750;
    const maxProducts = isDesktop ? 3 : 6;

    productItems.forEach((item, index) => {
      if (index >= maxProducts) {
        item.style.display = 'none';
      } else {
        item.style.display = '';
      }
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      // Clear highlights when clearing search term
      this.clearPopularSearchHighlights();
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    if (event) event.preventDefault();

    this.input.value = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.focus();

    // Clear popular search term highlights
    this.clearPopularSearchHighlights();

    if (this.modal) this.modal.classList.remove('searching');

    // Remove any existing results
    this.querySelector('#predictive-search-results-groups-wrapper')?.remove();

    // Remove is-empty class when search is cleared (back to initial state)
    const searchWrapper =
      this.closest('.search-modal')?.querySelector('.search-recommendations-wrapper') ||
      document.querySelector('.search-recommendations-wrapper');
    if (searchWrapper) {
      searchWrapper.classList.remove('is-empty');
    }
  }

  toggleEmptyState() {
    const searchWrapper =
      this.closest('.search-modal')?.querySelector('.search-recommendations-wrapper') ||
      document.querySelector('.search-recommendations-wrapper');

    if (!searchWrapper) return;

    // Get the input
    const input = this.closest('form')?.querySelector('.search__input');
    if (input && !input.value) {
      searchWrapper.classList.remove('results-empty');
      return;
    }

    // Check if there are any search results
    const hasProducts = this.querySelector('#predictive-search-results-products-list li');
    const hasQueries = this.querySelector('[data-predictive-search-type="query"]');

    const hasResults = hasProducts || hasQueries;

    // Toggle the is-empty class
    if (hasResults) {
      searchWrapper.classList.remove('results-empty');
    } else {
      searchWrapper.classList.add('results-empty');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
